import { useClient } from '@cortex/react-query';
import type { Workflow, WorkflowVersion } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
} from '@xyflow/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { readApiSaveErrors } from '../../lib/api-errors';
import { layoutHorizontalByLayer, positionsAreDegenerate } from '../../lib/auto-layout-graph';
import {
  isMcpConnectionUnauthenticated,
  nodeReferencesUnauthenticatedMcp,
} from '../../lib/mcp-auth-status';
import { useRunEventStreamInvalidation } from '../../lib/use-run-event-stream';
import { useRenameWorkflowNodeBindings } from '../../lib/workflow-node-binding';

import { buildIncomingWiringBadges } from './edge-wiring-chip';
import { diagnoseGraphHydration } from './graph-hydration-issue';
import { annotateEdgesWithWiring, edgesWiringEqual } from './node-configs/annotate-edges-wiring';
import { shouldAutoWireOnConnect, suggestAutoWire } from './node-configs/auto-wire';
import { getNodeTypeInputFieldNames } from './node-configs/effective-input-schema';
import { getEffectiveOutputFieldNamesByNodeKey } from './node-configs/effective-output-schema';
import {
  defaultOutputSchemaForNode,
  isEmptyOutputSchema,
  normalizeRegistryJsonSchema,
  normalizeRegistryOutputSchema,
} from './node-configs/node-type-schemas';
import { extractSaveWarnings, type ISaveWarning } from './save-warnings';
import { useLiveRun, type ILiveRunState } from './useLiveRun';
import { invalidateWorkflowQueries } from './workflow-invalidations';

/**
 * Shape exposed to consumers of `useWorkflowEditor()`. Designed so the canvas
 * (`WorkflowCanvas`) and the right panels (`right-panel/*`) share one source
 * of truth for the graph state without prop-drilling. Selection is NOT in
 * here — it lives in the route's URL search params so it's deep-linkable
 * and back-button-friendly; consumers read it from there directly.
 */
interface IWorkflowEditorState {
  workflow: Workflow;

  /**
   * The version whose graph is currently hydrated onto the canvas. When the
   * user opens a historical (non-current) version from the Versions panel,
   * this is that older version and `isReadOnly` is true — the canvas renders
   * it but editing is disabled. `null` means the canvas is showing the
   * workflow's current (editable) version.
   */
  viewingVersion: WorkflowVersion | null;
  /**
   * True while a historical version is loaded onto the canvas. Editing
   * affordances (drag, connect, delete, Save) are suppressed; the user must
   * Promote the version (or go Back to current) to edit. Run still works —
   * it's pinned to the viewed version.
   */
  isReadOnly: boolean;

  // Graph state, post-hydration from the displayed version.
  nodes: Node[];
  edges: Edge[];
  /** Nodes with `runStatus` mixed in from `live`, for canvas painting. */
  decoratedNodes: Node[];
  /** Edges with `animated` set on those touching a running node. */
  decoratedEdges: Edge[];

  // xyflow change handlers — bound to internal state.
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (conn: Connection) => void;

  /** Drop a new node onto the canvas at a sensible default position. */
  addNode: (nodeType: string) => string;
  /** Replace a single node's data (label / nodeType / config). */
  updateNode: (nodeKey: string, dataPatch: Record<string, unknown>) => void;
  /**
   * Rename a node's key. Updates the node's id + label AND rewires
   * every edge that touched the old key. Returns an error reason
   * (collision / empty) when the rename is refused. (DEV-16196 polish.)
   */
  renameNodeKey: (oldKey: string, nextKey: string) => { ok: true } | { ok: false; reason: string };
  /** Remove a node and any edges touching it. */
  removeNode: (nodeKey: string) => void;
  /** Look up a node by its key, post-decoration. */
  getNode: (nodeKey: string | null | undefined) => Node | null;
  /**
   * Node keys that exist in the workflow's persisted current version — i.e.
   * ones a rename/binding call to the API can target. A node added via
   * `addNode` isn't in here until the next successful save; the node
   * binding panel uses this to avoid querying bindings for a node the
   * backend doesn't know about yet (which 404s).
   */
  persistedNodeKeys: ReadonlySet<string>;

  /**
   * Locks of a Formant-managed workflow (`workflow.managedBy` set,
   * DEV-17471); empty for user-owned workflows. A locked node can't be
   * removed, renamed or retyped, and `lockedFieldsFor(nodeKey)` names its
   * read-only config fields (`*` = the whole config). The API enforces the
   * same rules with a 409 — these only keep the editor from offering edits
   * that would be refused.
   */
  lockedNodeKeys: ReadonlySet<string>;
  lockedFieldsFor: (nodeKey: string) => ReadonlySet<string>;
  isNodeLocked: (nodeKey: string) => boolean;

  // Dirty + validation surface.
  dirty: boolean;
  validationErrors: string[];
  /**
   * Advisory warnings from the LAST successful save (e.g. an llm-agent
   * toolRef that resolves to neither an MCP connection nor a platform tool).
   * Replaced on every save; a warning-free save clears them. Distinct from
   * `validationErrors` — the save committed. (DEV-16944.)
   */
  saveWarnings: ISaveWarning[];
  /** Clear the advisory-warnings banner (user hit ×). */
  dismissSaveWarnings: () => void;

  /**
   * Why the canvas is about to render nothing, when that blankness would be
   * misleading (DEV-17091 hypothesis 2): the versions fetch failed, the
   * workflow's currentVersionId doesn't resolve against its version history,
   * or the stored graph blob is malformed. Null when the canvas state is
   * self-explanatory (loading, healthy graph, or a genuinely empty workflow).
   */
  graphHydrationIssue: string | null;

  /**
   * The current WorkflowVersion's declared `inputSchema` (JSON Schema), or
   * null when the version has none. Surfaced here so node-config forms can
   * reason about what fields the run-trigger input will carry — needed by
   * the `llm-agent` form to decide whether a Handlebars `{{token}}`
   * will resolve when `inputMapping` is empty (in that case the trigger
   * input is passed through verbatim by `applyInputMapping`).
   */
  workflowInputSchema: Record<string, unknown> | null;

  /**
   * Node-type key → default outputSchema from `/v1/node-types/:key`. Used
   * to auto-seed graph nodes and resolve Connect-mode field lists.
   */
  nodeTypeOutputSchemaByKey: ReadonlyMap<string, Record<string, unknown> | null>;

  // Mutations exposed for the top action bar.
  save: ReturnType<typeof useSaveMutation>;
  /**
   * Cut a "one-off" / detached `WorkflowVersion` without updating the
   * workflow's `currentVersionId`. Lets the operator experiment with a
   * variant of the graph without disturbing the production version
   * everyone else is triggering. (DEV-16363.)
   */
  saveAsDetached: ReturnType<typeof useSaveAsDetachedMutation>;
  run: ReturnType<typeof useRunMutation>;
  /**
   * Convenience: save (if dirty) then run; otherwise just run. `input` is
   * forwarded to the run mutation as the signal payload (undefined → `{}`).
   * `workflowVersionId` pins the run to a specific (e.g. detached) version;
   * when set, the save-if-dirty step is SKIPPED — the caller asked for a
   * named version, so cutting a fresh one on top would be surprising. To
   * persist current edits while targeting a different version, use the
   * "Save as one-off" button first, then Run. (DEV-16363.)
   */
  saveAndRun: (args?: { input?: Record<string, unknown>; workflowVersionId?: string }) => void;

  // Live-run state — when the user clicks Run, we tail the new run via SSE.
  activeRunId: string | null;
  setActiveRunId: (id: string | null) => void;
  live: ILiveRunState;

  /**
   * Sidebar → canvas correspondence. When the run-detail sidebar
   * expands or clicks into a step, we stash its `nodeKey` here so the
   * canvas can mark the matching node with `.is-sidebar-focused`.
   * Independent of `activeRunId` and of canvas selection (`?panel=node`)
   * — focus does NOT change the right panel's URL state. Clear with
   * `null` when the sidebar closes. (DEV-16197.)
   */
  focusedNodeKey: string | null;
  setFocusedNodeKey: (key: string | null) => void;
}

const Ctx = createContext<IWorkflowEditorState | null>(null);

/** Shared empty set so `lockedFieldsFor` is referentially stable for unlocked nodes. */
const noLockedFields: ReadonlySet<string> = new Set<string>();

/**
 * Returns true when at least one change in the batch represents a mutation
 * that should mark the workflow as having unsaved edits. Excludes the
 * noise xyflow fires during normal interaction — selection toggles and
 * dimension measurements aren't persisted in the saved graph, so the
 * "Unsaved changes" indicator shouldn't react to them.
 */
function changesAreDirty(changes: Array<NodeChange | EdgeChange>): boolean {
  return changes.some((c) => c.type !== 'select' && c.type !== 'dimensions');
}

/**
 * Wrap the workflow detail page with this once `workflow` is loaded — every
 * descendant can then `useWorkflowEditor()` instead of plumbing the same
 * dozen pieces of state through props.
 */
export function WorkflowEditorProvider({
  workflow,
  viewVersionNumber,
  children,
}: {
  workflow: Workflow;
  /**
   * When set (from the `?version=N` search param), the canvas hydrates from
   * that version's frozen graph instead of the workflow's current version.
   * If it matches the current version (or doesn't resolve), we fall back to
   * the normal editable current-version view. (DEV — version viewer.)
   */
  viewVersionNumber?: number | null;
  children: ReactNode;
}) {
  const client = useClient();
  const qc = useQueryClient();

  const versions = useQuery({
    queryKey: ['workflow-versions', workflow.id],
    queryFn: () =>
      client.http.get(`v1/workflows/${workflow.id}/versions`).json<WorkflowVersion[]>(),
  });

  // Resolve which version the canvas should display. Default to the
  // workflow's current (editable) version; when `viewVersionNumber` points
  // at a *different* existing version, show that one read-only instead.
  const currentVersion = useMemo(
    () => versions.data?.find((v) => v.id === workflow.currentVersionId) ?? null,
    [versions.data, workflow.currentVersionId],
  );
  const requestedVersion = useMemo(
    () =>
      viewVersionNumber != null
        ? (versions.data?.find((v) => v.versionNumber === viewVersionNumber) ?? null)
        : null,
    [versions.data, viewVersionNumber],
  );
  const displayedVersion = requestedVersion ?? currentVersion;

  // Name the cause of an otherwise-silent blank canvas (DEV-17091): the old
  // hydration path bailed with `if (!current) return;` and rendered an empty
  // grid indistinguishable from an empty workflow.
  const graphHydrationIssue = useMemo(
    () =>
      diagnoseGraphHydration({
        versionsStatus: versions.status,
        // Post-save, the fresh workflow row joins a stale versions list for
        // one refetch round-trip; `status` stays 'success' through it, so
        // the classifier needs the fetching flag to not call that window a
        // data inconsistency (it did — on every successful save).
        versionsFetching: versions.isFetching,
        versionsErrorMessage: (versions.error as Error | null)?.message,
        versions: versions.data?.map((v) => ({
          id: v.id,
          versionNumber: v.versionNumber,
          nodeCount: Array.isArray((v.graph as { nodes?: unknown } | null)?.nodes)
            ? (v.graph as { nodes: unknown[] }).nodes.length
            : null,
        })),
        currentVersionId: workflow.currentVersionId ?? null,
        requestedVersionNumber: viewVersionNumber ?? null,
        displayedVersionGraph: displayedVersion?.graph,
        hasDisplayedVersion: displayedVersion != null,
      }),
    [
      versions.status,
      versions.isFetching,
      versions.error,
      versions.data,
      workflow.currentVersionId,
      viewVersionNumber,
      displayedVersion,
    ],
  );
  const isReadOnly = displayedVersion != null && displayedVersion.id !== workflow.currentVersionId;
  const viewingVersion = isReadOnly ? displayedVersion : null;

  const persistedNodeKeys = useMemo(() => {
    const graph = currentVersion?.graph as { nodes?: Array<Record<string, unknown>> } | undefined;
    return new Set((graph?.nodes ?? []).map((n, i) => String(n.nodeKey ?? n.id ?? `n-${i}`)));
  }, [currentVersion]);

  // Row-level edges for the current version (with stable cuid `id`s).
  // Driven separately from the graph JSON blob — the blob doesn't carry
  // row ids, but the edge inspector needs them to call PATCH
  // /workflows/:id/edges/:edgeId. (DEV-16183.)
  const currentEdges = useQuery({
    queryKey: ['workflow', workflow.id, 'edges'],
    queryFn: () =>
      client.http.get(`v1/workflows/${workflow.id}/edges`).json<
        Array<{
          id: string;
          fromNodeKey: string;
          toNodeKey: string;
          condition: string | null;
          boardColumnId: string | null;
        }>
      >(),
  });

  // Lookup tables for the per-type meta line on canvas nodes. Reuses the
  // same query keys as `LlmAgentConfig` / `McpQueryConfig` so the inspector
  // and the canvas share one cache entry per list. Small lists (caps at
  // 200), so the eager load on editor mount is cheap.
  const connectionList = useQuery({
    queryKey: ['mcp-connections', 'for-node-config'],
    queryFn: () =>
      // `authType` + `hasKeyring` are the two fields the canvas needs to flag a
      // node whose MCP isn't connected yet (see `unauthedConnectionNames`). The
      // endpoint already returns full rows; we just widen the type to read them.
      client.http.get('v1/mcp-connections?limit=200').json<{
        items: Array<{
          id: string;
          name: string;
          authType: 'none' | 'oauth';
          hasKeyring: boolean;
        }>;
        total: number;
      }>(),
  });

  // `McpQueryConfig` accepts either id or name as `connectionRef`, so we
  // index both forms here to mirror that selection semantic — otherwise a
  // node whose saved ref is a name (older graphs) would render as
  // unresolved.
  const connectionNameByRef = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of connectionList.data?.items ?? []) {
      m.set(c.id, c.name);
      m.set(c.name, c.name);
    }
    return m;
  }, [connectionList.data]);

  // Names of MCP connections that are configured but not yet callable (an
  // `oauth` connection with no keyring). An `llm-agent` node whose `toolRefs`
  // name one of these will silently fail at run time — the canvas surfaces a
  // caution glyph on that node so the problem is visible on the graph, not just
  // buried in the node's config panel (DEV-17204).
  //
  // Only populated once the query has SUCCEEDED — while it's in flight the map
  // is empty, so a valid node is never briefly flagged on a slow load (the same
  // loading-guard rule the tool-ref picker uses).
  const unauthedConnectionNames = useMemo(() => {
    const s = new Set<string>();
    if (!connectionList.isSuccess) return s;
    for (const c of connectionList.data.items) {
      if (isMcpConnectionUnauthenticated(c)) s.add(c.name);
    }
    return s;
  }, [connectionList.isSuccess, connectionList.data]);

  const nodeTypeSchemasQuery = useQuery({
    queryKey: ['node-type-schemas'],
    queryFn: async () => {
      const list = await client.http.get('v1/node-types').json<Array<{ key: string }>>();
      const entries = await Promise.all(
        list.map(async (t) => {
          const detail = await client.http.get(`v1/node-types/${t.key}`).json<{
            key: string;
            inputSchema: Record<string, unknown>;
            outputSchema: Record<string, unknown>;
          }>();
          return [
            t.key,
            {
              input: normalizeRegistryJsonSchema(detail.inputSchema) ?? detail.inputSchema,
              output: normalizeRegistryOutputSchema(detail.outputSchema) ?? detail.outputSchema,
            },
          ] as const;
        }),
      );
      const input = new Map<string, Record<string, unknown>>();
      const output = new Map<string, Record<string, unknown>>();
      for (const [key, schemas] of entries) {
        input.set(key, schemas.input);
        output.set(key, schemas.output);
      }
      return { input, output };
    },
  });

  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [dirty, setDirty] = useState(false);
  // originalNodeKey -> current (post-rename) key, for keys renamed since the
  // last save. Drained by `useSaveMutation`'s onSuccess, which migrates each
  // pair's bindings then clears this. See `renameNodeKey`.
  const pendingRenamesRef = useRef<Map<string, string>>(new Map());
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [saveWarnings, setSaveWarnings] = useState<ISaveWarning[]>([]);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [focusedNodeKey, setFocusedNodeKey] = useState<string | null>(null);
  const live = useLiveRun(activeRunId);
  // SSE-driven invalidation: while a run is active, each `node.started`,
  // `node.completed`, etc. event from the runner invalidates the
  // `useLiveRun` queries so the canvas's amber `is-running` outline and
  // marching-ants `is-next` outlines flip on/off near-instantly instead
  // of waiting up to 1.5s for the next polling tick. Lives in the editor
  // context (not the side panel) so the canvas keeps live updates even
  // when the runs panel is closed. (DEV-16156.)
  useRunEventStreamInvalidation(activeRunId, !live.isTerminal);

  // Locks of a Formant-managed workflow. Keyed on content, not identity: the
  // workflow row is re-fetched after every save and the arrays come back as
  // new objects each time.
  const lockedNodeKeysSig = JSON.stringify(workflow.lockedNodeKeys ?? []);
  const lockedFieldsSig = JSON.stringify(workflow.lockedFields ?? {});
  const lockedNodeKeys = useMemo<ReadonlySet<string>>(
    () => new Set(JSON.parse(lockedNodeKeysSig) as string[]),
    [lockedNodeKeysSig],
  );
  const lockedFieldsByNode = useMemo(() => {
    const map = new Map<string, ReadonlySet<string>>();
    const byNode = JSON.parse(lockedFieldsSig) as Record<string, string[]>;
    for (const [nodeKey, fields] of Object.entries(byNode)) {
      map.set(nodeKey, new Set(fields));
    }
    return map;
  }, [lockedFieldsSig]);
  const lockedFieldsFor = useCallback(
    (nodeKey: string): ReadonlySet<string> =>
      // Fields are only enforced on locked nodes — mirror the server rule.
      (lockedNodeKeys.has(nodeKey) ? lockedFieldsByNode.get(nodeKey) : undefined) ?? noLockedFields,
    [lockedNodeKeys, lockedFieldsByNode],
  );
  const isNodeLocked = useCallback(
    (nodeKey: string): boolean => lockedNodeKeys.has(nodeKey),
    [lockedNodeKeys],
  );

  // Hydrate xyflow state from the displayed version exactly once per version
  // load. The displayed version is the workflow's current version normally,
  // or a historical version when the user is viewing one read-only.
  useEffect(() => {
    const current = displayedVersion;
    if (!current) return;
    const schemaMap = nodeTypeSchemasQuery.data?.output;
    const graph = current.graph as
      | { nodes?: Array<Record<string, unknown>>; edges?: Array<Record<string, unknown>> }
      | undefined;
    // Detect "Claude / API caller didn't set positions" — every node
    // shares the same (x, y) (typically `{0, 0}`) — and auto-layout the
    // graph horizontally using a topological sort. Interactively-
    // authored workflows have varied positions; we leave those alone.
    // (DEV-16181.)
    const rawNodes = graph?.nodes ?? [];
    const positionablesForLayout = rawNodes.map((n, i) => ({
      nodeKey: String(n.nodeKey ?? n.id ?? `n-${i}`),
      position: n.position as { x: number; y: number } | null | undefined,
    }));
    const computedPositions = positionsAreDegenerate(positionablesForLayout)
      ? layoutHorizontalByLayer(
          positionablesForLayout,
          (graph?.edges ?? []).map((e) => ({
            fromNodeKey: String((e as { fromNodeKey?: unknown }).fromNodeKey ?? ''),
            toNodeKey: String((e as { toNodeKey?: unknown }).toNodeKey ?? ''),
          })),
        )
      : null;
    const ns: Node[] = rawNodes.map((n, i) => {
      const nodeKey = String(n.nodeKey ?? n.id ?? `n-${i}`);
      const nodeType = String(n.nodeType ?? 'unknown');
      const outputMapping =
        typeof n.outputMapping === 'string' && n.outputMapping.length > 0 ? n.outputMapping : null;
      // A locked node of a managed workflow round-trips its stored schema
      // verbatim: materialising the registry default (or unwrapping a $ref)
      // would read as a structure change on save, which the API refuses.
      const locked = lockedNodeKeys.has(nodeKey);
      const storedOutputSchema = (n.outputSchema as Record<string, unknown> | undefined) ?? {};
      let outputSchema = locked
        ? storedOutputSchema
        : (normalizeRegistryOutputSchema(storedOutputSchema) ?? {});
      if (schemaMap && !locked) {
        const def = defaultOutputSchemaForNode(nodeType, outputMapping, schemaMap);
        if (def && isEmptyOutputSchema(outputSchema)) outputSchema = def;
      }
      const saved = n.position as { x: number; y: number } | null | undefined;
      const computed = computedPositions?.[nodeKey];
      const fallbackGrid = { x: (i % 4) * 220 + 40, y: Math.floor(i / 4) * 140 + 40 };
      return {
        id: nodeKey,
        data: {
          label: String(n.nodeKey ?? n.id ?? `Node ${i + 1}`),
          nodeType,
          config: n.config ?? {},
          inputMapping: (n.inputMapping as Record<string, unknown> | undefined) ?? {},
          outputSchema,
          outputMapping,
        },
        position: computed ?? saved ?? fallbackGrid,
        type: 'default',
      };
    });
    // Prefer the row-level edges (with real cuid ids) when they're
    // available — that's what the edge inspector PATCHes. Fall back to
    // the graph-blob edges if the side query hasn't loaded yet, so the
    // canvas still renders synchronously on mount.
    //
    // The `/edges` query only reflects the CURRENT version's rows, so when
    // viewing a historical version read-only we must read the version's own
    // frozen blob edges instead — otherwise the old graph would render with
    // the current version's wiring.
    const rowEdges = isReadOnly ? undefined : currentEdges.data;
    const es: Edge[] = rowEdges
      ? rowEdges.map((e) => ({
          id: e.id,
          source: e.fromNodeKey,
          target: e.toNodeKey,
          label: e.condition ?? undefined,
          data: {
            condition: e.condition,
            boardColumnId: e.boardColumnId,
          },
        }))
      : (graph?.edges ?? []).map((e, i) => ({
          id: `e-${i}`,
          source: String(e.fromNodeKey ?? ''),
          target: String(e.toNodeKey ?? ''),
          label: e.condition ? String(e.condition) : undefined,
        }));
    setNodes(ns);
    setEdges(es);
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    displayedVersion?.id,
    isReadOnly,
    versions.data?.length,
    currentEdges.data,
    nodeTypeSchemasQuery.data?.output,
  ]);

  const nodeTypeSchemaMap = useMemo(
    () => nodeTypeSchemasQuery.data?.output ?? new Map<string, Record<string, unknown>>(),
    [nodeTypeSchemasQuery.data?.output],
  );

  const nodeTypeInputSchemaMap = useMemo(
    () => nodeTypeSchemasQuery.data?.input ?? new Map<string, Record<string, unknown>>(),
    [nodeTypeSchemasQuery.data?.input],
  );

  // If a node was added before node-type schemas finished loading, backfill
  // its outputSchema once the cache arrives — same as if the author had
  // typed the default in manually.
  useEffect(() => {
    const schemaMap = nodeTypeSchemasQuery.data?.output;
    if (!schemaMap || schemaMap.size === 0) return;
    setNodes((ns) => {
      let changed = false;
      const next = ns.map((n) => {
        const data = n.data as Record<string, unknown>;
        const nodeType = String(data.nodeType ?? '');
        const outputMapping = data.outputMapping as string | null | undefined;
        const outputSchema = (data.outputSchema ?? {}) as Record<string, unknown>;
        const def = defaultOutputSchemaForNode(nodeType, outputMapping ?? null, schemaMap);
        // Never backfill a locked node — see the hydration note above.
        if (def && isEmptyOutputSchema(outputSchema) && !lockedNodeKeys.has(n.id)) {
          changed = true;
          return { ...n, data: { ...data, outputSchema: def } };
        }
        return n;
      });
      if (changed) {
        setDirty(true);
        return next;
      }
      return ns;
    });
  }, [nodeTypeSchemasQuery.data?.output, lockedNodeKeys]);

  const outputSchemaCtx = useMemo(
    () => ({
      nodeTypeOutputSchemaByKey: nodeTypeSchemaMap,
    }),
    [nodeTypeSchemaMap],
  );

  // Recompute edge wiring chips from persisted inputMapping + schemas so
  // indicators survive save/reload and stay in sync when mappings change.
  const wiringGraphSig = useMemo(
    () =>
      JSON.stringify({
        nodes: nodes.map((n) => {
          const d = n.data as Record<string, unknown>;
          return {
            id: n.id,
            nodeType: d.nodeType,
            inputMapping: d.inputMapping,
            outputSchema: d.outputSchema,
            outputMapping: d.outputMapping,
            config: d.config,
          };
        }),
        edges: edges.map((e) => ({ id: e.id, source: e.source, target: e.target })),
      }),
    [nodes, edges],
  );

  useEffect(() => {
    if (nodeTypeInputSchemaMap.size === 0 || nodes.length === 0 || edges.length === 0) return;
    setEdges((es) => {
      const next = annotateEdgesWithWiring(es, nodes, nodeTypeInputSchemaMap, outputSchemaCtx);
      return edgesWiringEqual(es, next) ? es : next;
    });
  }, [wiringGraphSig, nodeTypeInputSchemaMap, outputSchemaCtx, nodes, edges.length]);

  const onNodesChange = useCallback(
    (allChanges: NodeChange[]) => {
      // The DEL key on a selected node arrives here as a `remove` change;
      // a locked node of a managed workflow stays put.
      const changes =
        lockedNodeKeys.size === 0
          ? allChanges
          : allChanges.filter((c) => !(c.type === 'remove' && lockedNodeKeys.has(c.id)));
      if (changes.length === 0) return;
      setNodes((ns) => applyNodeChanges(changes, ns));
      // xyflow surfaces deletions as `remove` changes through this handler
      // (e.g. from the DEL key on a selected node). The node mutation alone
      // would leave dangling edges, so cascade-remove anything pointing at
      // a removed node here — same invariant the explicit `removeNode`
      // path enforces.
      const removedNodeKeys = changes
        .filter((c): c is NodeChange & { type: 'remove'; id: string } => c.type === 'remove')
        .map((c) => c.id);
      if (removedNodeKeys.length > 0) {
        const removed = new Set(removedNodeKeys);
        setEdges((es) => es.filter((e) => !removed.has(e.source) && !removed.has(e.target)));
      }
      // Only mark the workflow dirty for changes that actually mutate the
      // saved graph shape. xyflow also fires `select` / `dimensions` changes
      // constantly during normal interaction (clicks, layout measurements),
      // which would otherwise re-set the "Unsaved changes" indicator the
      // instant a save completed. (DEV-16141.)
      if (changesAreDirty(changes)) setDirty(true);
    },
    [lockedNodeKeys],
  );
  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    setEdges((es) => applyEdgeChanges(changes, es));
    // Unplugging an edge never mutates the target's inputMapping. Plugging a
    // different upstream after an unplug also skips auto-wire when orphaned
    // refs remain — the author clears or edits Inputs manually.
    if (changesAreDirty(changes)) setDirty(true);
  }, []);
  const onConnect = useCallback(
    (conn: Connection) => {
      const sourceKey = conn.source;
      const targetKey = conn.target;
      if (!sourceKey || !targetKey) return;

      const schemaCtx = outputSchemaCtx;

      setNodes((ns) => {
        const source = ns.find((n) => n.id === sourceKey);
        const target = ns.find((n) => n.id === targetKey);
        if (!source || !target) return ns;

        const targetData = target.data as {
          nodeType?: string;
          inputMapping?: Record<string, unknown>;
        };
        const targetNodeType = String(targetData.nodeType ?? '');
        const existingMapping = (targetData.inputMapping ?? {}) as Record<string, unknown>;
        const connectedSourcesToTarget = new Set(
          edges.filter((e) => e.target === targetKey).map((e) => e.source),
        );
        connectedSourcesToTarget.add(sourceKey);

        const edgeId = `e-${sourceKey}-${targetKey}-${Date.now()}`;

        setEdges((es) =>
          addEdge(
            {
              ...conn,
              id: edgeId,
            },
            es,
          ),
        );

        if (
          !shouldAutoWireOnConnect({
            sourceKey,
            existingMapping,
            connectedSourcesToTarget,
          })
        ) {
          return ns;
        }

        const sourceFields = getEffectiveOutputFieldNamesByNodeKey(
          (k) => ns.find((n) => n.id === k) ?? null,
          sourceKey,
          schemaCtx,
        );
        const targetInputFields = getNodeTypeInputFieldNames(
          targetNodeType,
          nodeTypeInputSchemaMap,
        );
        const { patches } = suggestAutoWire({
          sourceKey,
          sourceFields,
          targetInputFields,
          existingMapping,
          connectedSourcesToTarget,
        });

        if (Object.keys(patches).length === 0) return ns;

        return ns.map((n) =>
          n.id === targetKey
            ? {
                ...n,
                data: {
                  ...targetData,
                  inputMapping: { ...existingMapping, ...patches },
                },
              }
            : n,
        );
      });
      setDirty(true);
    },
    [outputSchemaCtx, nodeTypeInputSchemaMap, edges],
  );

  const addNode = useCallback(
    (nodeType: string): string => {
      if (isReadOnly) return '';
      const key = `${nodeType.replace(/[^a-z0-9-]/giu, '-').toLowerCase()}-${nodes.length + 1}`;
      const def = defaultOutputSchemaForNode(nodeType, null, nodeTypeSchemaMap);
      setNodes((ns) => [
        ...ns,
        {
          id: key,
          data: {
            label: key,
            nodeType,
            config: {},
            inputMapping: {},
            outputMapping: null,
            ...(def ? { outputSchema: def } : {}),
          },
          position: { x: 80 + ns.length * 30, y: 80 + ns.length * 30 },
          type: 'default',
        },
      ]);
      setDirty(true);
      return key;
    },
    [isReadOnly, nodes.length, nodeTypeSchemaMap],
  );

  const updateNode = useCallback(
    (nodeKey: string, dataPatch: Record<string, unknown>) => {
      // Read-only guard: viewing a historical version. Several inline
      // node-config forms call this directly, so the only reliable place to
      // stop a stray write is here. (UI controls are disabled too.)
      if (isReadOnly) return;
      setNodes((ns) =>
        ns.map((n) =>
          n.id === nodeKey
            ? { ...n, data: { ...(n.data as Record<string, unknown>), ...dataPatch } }
            : n,
        ),
      );
      setDirty(true);
    },
    [isReadOnly],
  );

  const removeNode = useCallback(
    (nodeKey: string) => {
      if (isReadOnly || lockedNodeKeys.has(nodeKey)) return;
      setNodes((ns) => ns.filter((n) => n.id !== nodeKey));
      setEdges((es) => es.filter((e) => e.source !== nodeKey && e.target !== nodeKey));
      setDirty(true);
    },
    [isReadOnly, lockedNodeKeys],
  );

  /**
   * Rename a node's key. The key is the load-bearing identifier across
   * the whole workflow:
   *   - xyflow's `node.id` (drives selection, edges' source/target,
   *     and the canvas DOM)
   *   - `data.label` (what the kit node renders)
   *   - `WorkflowEdge.fromNodeKey` / `toNodeKey` for every edge that
   *     touches the node
   *   - The persisted `WorkflowNode.nodeKey` (after the next save)
   *
   * Returns `{ ok: true }` on success or `{ ok: false, reason }` when
   * the rename is rejected — caller renders the message inline. We
   * refuse empties, no-ops (same key), and collisions with another
   * node's existing key so the graph never ends up with two nodes
   * sharing an identifier.
   */
  const renameNodeKey = useCallback(
    (oldKey: string, nextKey: string): { ok: true } | { ok: false; reason: string } => {
      if (isReadOnly) return { ok: false, reason: 'This version is read-only.' };
      if (lockedNodeKeys.has(oldKey)) {
        return { ok: false, reason: 'Managed by Formant — this node is locked' };
      }
      const trimmed = nextKey.trim();
      if (trimmed.length === 0) {
        return { ok: false, reason: 'Node key cannot be empty.' };
      }
      if (trimmed === oldKey) return { ok: true };
      const collides = nodes.some((n) => n.id === trimmed);
      if (collides) {
        return { ok: false, reason: `A node with key "${trimmed}" already exists.` };
      }
      setNodes((ns) =>
        ns.map((n) =>
          n.id === oldKey
            ? {
                ...n,
                id: trimmed,
                data: { ...(n.data as Record<string, unknown>), label: trimmed },
              }
            : n,
        ),
      );
      setEdges((es) =>
        es.map((e) => {
          if (e.source !== oldKey && e.target !== oldKey) return e;
          return {
            ...e,
            source: e.source === oldKey ? trimmed : e.source,
            target: e.target === oldKey ? trimmed : e.target,
          };
        }),
      );
      setDirty(true);

      // Bindings are keyed by nodeKey server-side. Track the rename (folding
      // multi-hop chains down to original→final, and dropping the entry if
      // the chain renames back to its original key) so the next successful
      // save can migrate the binding rows — otherwise they're silently
      // orphaned under the old key. Only worth tracking when the old key
      // could actually have bindings, i.e. it's a persisted key or already
      // mid-chain; a brand-new unsaved node was never bindable under its
      // previous key.
      const pending = pendingRenamesRef.current;
      const originalKey =
        [...pending.entries()].find(([, current]) => current === oldKey)?.[0] ??
        (persistedNodeKeys.has(oldKey) ? oldKey : null);
      if (originalKey) {
        if (trimmed === originalKey) {
          pending.delete(originalKey);
        } else {
          pending.set(originalKey, trimmed);
        }
      }

      return { ok: true };
    },
    [isReadOnly, lockedNodeKeys, nodes, persistedNodeKeys],
  );

  const renameBindings = useRenameWorkflowNodeBindings(workflow.id);
  const save = useSaveMutation({
    client,
    qc,
    workflowId: workflow.id,
    nodes,
    edges,
    onResetDirty: () => setDirty(false),
    onValidationErrors: setValidationErrors,
    onWarnings: setSaveWarnings,
    onSaved: () => {
      const pending = pendingRenamesRef.current;
      if (pending.size === 0) return;
      const renames = [...pending.entries()];
      pending.clear();
      for (const [oldNodeKey, newNodeKey] of renames) {
        renameBindings.mutate(
          { oldNodeKey, newNodeKey },
          {
            // A migration failure here shouldn't reopen the pending-rename
            // map (a retry would re-send a rename the user never repeated)
            // — surface it so the operator can rebind manually if needed.
            onError: (err) => console.error('Failed to migrate node bindings after rename', err),
          },
        );
      }
    },
  });

  const saveAsDetached = useSaveAsDetachedMutation({
    client,
    qc,
    workflowId: workflow.id,
    nodes,
    edges,
    onValidationErrors: setValidationErrors,
    onWarnings: setSaveWarnings,
  });

  const run = useRunMutation({
    client,
    qc,
    workflowId: workflow.id,
    onRunStarted: setActiveRunId,
  });

  const saveAndRun = useCallback(
    (args?: { input?: Record<string, unknown>; workflowVersionId?: string }) => {
      const input = args?.input;
      const workflowVersionId = args?.workflowVersionId;
      // When the caller pinned a specific version, skip save — they asked
      // for that version explicitly. (DEV-16363.)
      if (workflowVersionId) {
        run.mutate({ input, workflowVersionId });
        return;
      }
      if (dirty) {
        save.mutate(undefined, { onSuccess: () => run.mutate({ input }) });
      } else {
        run.mutate({ input });
      }
    },
    [dirty, save, run],
  );

  // Decorate nodes with live runStatus so the canvas can paint state classes.
  // Also mark "next" nodes — downstream targets of any currently-running node
  // that haven't already run — so the canvas can render the marching-ants
  // outline for upcoming work. (DEV-16131.)
  const decoratedNodes = useMemo(() => {
    const statuses = live.statusByNodeKey;
    const runningIds = new Set<string>();
    const settledIds = new Set<string>();
    for (const [id, s] of Object.entries(statuses)) {
      if (s === 'RUNNING') runningIds.add(id);
      else if (s === 'COMPLETED' || s === 'FAILED' || s === 'SKIPPED') settledIds.add(id);
    }
    const nextIds = new Set<string>();
    if (runningIds.size > 0) {
      for (const e of edges) {
        if (runningIds.has(e.source) && !runningIds.has(e.target) && !settledIds.has(e.target)) {
          nextIds.add(e.target);
        }
      }
    }
    const durations = live.durationByNodeKey;
    return nodes.map((n) => {
      const status = statuses[n.id];
      const isNext = nextIds.has(n.id);
      const isFocused = focusedNodeKey === n.id;
      const runDurationMs = durations[n.id];
      const baseData = n.data as Record<string, unknown>;
      const nodeType = typeof baseData.nodeType === 'string' ? baseData.nodeType : '';
      const config = (baseData.config ?? {}) as Record<string, unknown>;
      const incomingWiring = buildIncomingWiringBadges(n.id, edges);
      // Resolve per-type display names so the canvas component can render
      // human-friendly meta lines without firing its own queries per node.
      // We only set the field when a resolved match exists — the consumer
      // falls back to showing the raw id when undefined, which makes the
      // unresolved case visually obvious instead of silently blank.
      let resolvedConnectionName: string | undefined;
      if (nodeType === 'mcp-query' && typeof config.connectionRef === 'string') {
        resolvedConnectionName = connectionNameByRef.get(config.connectionRef);
      }
      // An llm-agent node references its MCPs by name in `config.toolRefs`.
      // Flag the node when any referenced connection is configured but not yet
      // authenticated, so the canvas can render a caution glyph (DEV-17204).
      const hasUnauthenticatedMcp = nodeReferencesUnauthenticatedMcp(
        nodeType,
        config,
        unauthedConnectionNames,
      );
      if (
        !status &&
        !isNext &&
        !isFocused &&
        !resolvedConnectionName &&
        !hasUnauthenticatedMcp &&
        runDurationMs === undefined &&
        incomingWiring.length === 0
      ) {
        return n;
      }
      return {
        ...n,
        data: {
          ...baseData,
          runStatus: status,
          isNext,
          isFocused,
          resolvedConnectionName,
          hasUnauthenticatedMcp,
          runDurationMs,
          ...(incomingWiring.length > 0 ? { incomingWiring } : {}),
        },
      };
    });
  }, [
    nodes,
    edges,
    focusedNodeKey,
    live.statusByNodeKey,
    live.durationByNodeKey,
    connectionNameByRef,
    unauthedConnectionNames,
  ]);

  // Flow an animated dash along the WHOLE path the run has traversed —
  // not just the running node's neighbours. An edge animates once BOTH
  // endpoints have been activated (RUNNING or a terminal state other
  // than SKIPPED), which lights the path cumulatively from start to the
  // current frontier and naturally skips branches that weren't taken
  // (their nodes never activate). The dash is xyflow's built-in
  // `.animated` dashdraw, tinted blue in workflow-editor.css to match
  // the running-node glow. When nothing has run we return the base
  // `edges` reference unchanged — no render churn, edges stay static.
  // (DEV-16296 follow-up — animated execution path.)
  const decoratedEdges = useMemo(() => {
    const statuses = live.statusByNodeKey;
    const onPath = new Set<string>();
    let anyRunning = false;
    for (const [id, s] of Object.entries(statuses)) {
      if (s === 'RUNNING') {
        onPath.add(id);
        anyRunning = true;
      } else if (s === 'COMPLETED' || s === 'FAILED') {
        onPath.add(id);
      }
    }
    // Only flow the path while the run is actually in progress (some node
    // is RUNNING). Once the run is terminal — every path node COMPLETED —
    // there's no live work to show, so the edges fall back to static.
    if (!anyRunning) return edges;
    return edges.map((e) =>
      onPath.has(e.source) && onPath.has(e.target) ? { ...e, animated: true } : e,
    );
  }, [edges, live.statusByNodeKey]);

  const getNode = useCallback(
    (nodeKey: string | null | undefined) =>
      nodeKey ? (decoratedNodes.find((n) => n.id === nodeKey) ?? null) : null,
    [decoratedNodes],
  );

  // Derived from the loaded versions list; null while loading or when the
  // version has no declared schema. Consumers treat null as "we don't know".
  const workflowInputSchema = useMemo<Record<string, unknown> | null>(() => {
    const current = versions.data?.find((v) => v.id === workflow.currentVersionId);
    return (current?.inputSchema as Record<string, unknown> | null | undefined) ?? null;
  }, [versions.data, workflow.currentVersionId]);

  const value: IWorkflowEditorState = {
    workflow,
    viewingVersion,
    isReadOnly,
    nodes,
    edges,
    decoratedNodes,
    decoratedEdges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addNode,
    updateNode,
    renameNodeKey,
    removeNode,
    getNode,
    persistedNodeKeys,
    lockedNodeKeys,
    lockedFieldsFor,
    isNodeLocked,
    dirty,
    validationErrors,
    saveWarnings,
    dismissSaveWarnings: () => setSaveWarnings([]),
    graphHydrationIssue,
    workflowInputSchema,
    nodeTypeOutputSchemaByKey: nodeTypeSchemaMap,
    save,
    saveAsDetached,
    run,
    saveAndRun,
    activeRunId,
    setActiveRunId,
    live,
    focusedNodeKey,
    setFocusedNodeKey,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Optional variant of `useWorkflowEditor` — returns `null` when the
 * caller is rendered OUTSIDE `WorkflowEditorProvider`. Used by surfaces
 * shared with the standalone triage run-detail page (`RunExecutionTrace`
 * sits in both the workflow builder side panel AND the `/triage/runs/:id`
 * page; the latter has no editor context to talk back to).
 *
 * The default `useWorkflowEditor()` throws — keep using that everywhere
 * inside the builder; this is strictly for components that legitimately
 * render in both worlds.
 */
export function useWorkflowEditorOptional(): IWorkflowEditorState | null {
  return useContext(Ctx);
}

/**
 * Hook every editor consumer goes through. Throws if used outside the
 * provider — that's intentional, the alternative would be a bunch of
 * defensive `?.` chains on every callsite.
 */
export function useWorkflowEditor(): IWorkflowEditorState {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error('useWorkflowEditor must be called inside <WorkflowEditorProvider>');
  }
  return ctx;
}

// ─── Mutation hooks (kept private to the context module) ────────────────────

function useSaveMutation(args: {
  client: ReturnType<typeof useClient>;
  qc: ReturnType<typeof useQueryClient>;
  workflowId: string;
  nodes: Node[];
  edges: Edge[];
  onResetDirty: () => void;
  onValidationErrors: (lines: string[]) => void;
  /**
   * Fired on every successful save with the response's advisory `warnings`
   * (empty array when the field is absent — i.e. a clean save). (DEV-16944.)
   */
  onWarnings: (warnings: ISaveWarning[]) => void;
  /** Fired after the patch commits and caches are invalidated. */
  onSaved?: () => void;
}) {
  const {
    client,
    qc,
    workflowId,
    nodes,
    edges,
    onResetDirty,
    onValidationErrors,
    onWarnings,
    onSaved,
  } = args;
  return useMutation({
    mutationFn: () =>
      client.http
        .patch(`v1/workflows/${workflowId}`, {
          json: {
            graph: {
              nodes: nodes.map((n) => {
                const d = n.data as {
                  nodeType?: string;
                  config?: Record<string, unknown>;
                  inputMapping?: Record<string, unknown>;
                  outputSchema?: Record<string, unknown>;
                  outputMapping?: string | null;
                };
                const base: Record<string, unknown> = {
                  nodeKey: n.id,
                  nodeType: String(d.nodeType ?? 'unknown'),
                  config: d.config ?? {},
                  inputMapping: d.inputMapping ?? {},
                  outputSchema: d.outputSchema ?? {},
                  position: { x: n.position.x, y: n.position.y },
                };
                // Only include outputMapping when set — the wire schema
                // treats it as optional and `null` would be coerced away
                // by JSON anyway. (See WorkflowNodeSchema.)
                if (typeof d.outputMapping === 'string' && d.outputMapping.length > 0) {
                  base.outputMapping = d.outputMapping;
                }
                return base;
              }),
              edges: edges.map((e) => ({
                fromNodeKey: e.source,
                toNodeKey: e.target,
                condition: typeof e.label === 'string' ? e.label : undefined,
              })),
            },
          },
        })
        // The API may append top-level advisory `warnings` (only when
        // non-empty; absent on older servers) — carried alongside the
        // Workflow row, not part of it. (DEV-16944.)
        .json<Workflow & { warnings?: ISaveWarning[] }>(),
    onMutate: () => {
      onValidationErrors([]);
    },
    onSuccess: async (res) => {
      onResetDirty();
      onValidationErrors([]);
      // Replace-on-save semantics: a clean save yields [] and clears the
      // banner; a save with warnings swaps in the fresh set.
      onWarnings(extractSaveWarnings(res));
      // Batched invalidation (see workflow-invalidations.ts): workflow row +
      // versions list in the same tick, or the hydration classifier misreads
      // the stale join as a data inconsistency. The extra ['workflows'] key:
      // the list page caches per-workflow fields the builder can mutate —
      // title, updatedAt, currentVersionId, etc. Without it the user saves
      // in the builder, navigates back, and sees stale data. DEV-16157.
      try {
        await invalidateWorkflowQueries(qc, workflowId, [['workflows']]);
      } catch (err) {
        console.error(err);
      }
      onSaved?.();
    },
    onError: (err) => {
      // Surface BE per-field validation lines so the inspector can render
      // them next to the offending node. Falls back to the bare error
      // message when the response isn't the structured shape.
      readApiSaveErrors(err)
        .then((lines) => onValidationErrors(lines))
        .catch(() => onValidationErrors([]));
    },
  });
}

/**
 * `POST /v1/workflows/:id/versions` — cuts a "one-off" DRAFT `WorkflowVersion`
 * that does NOT become the workflow's current. Same graph payload shape as
 * `useSaveMutation`. Only the versions cache is invalidated — the workflow
 * row itself (and its `currentVersion`) didn't change. (DEV-16363.)
 */
function useSaveAsDetachedMutation(args: {
  client: ReturnType<typeof useClient>;
  qc: ReturnType<typeof useQueryClient>;
  workflowId: string;
  nodes: Node[];
  edges: Edge[];
  onValidationErrors: (lines: string[]) => void;
  /** Same advisory-warnings contract as `useSaveMutation`. (DEV-16944.) */
  onWarnings: (warnings: ISaveWarning[]) => void;
}) {
  const { client, qc, workflowId, nodes, edges, onValidationErrors, onWarnings } = args;
  return useMutation({
    mutationFn: () =>
      client.http
        .post(`v1/workflows/${workflowId}/versions`, {
          json: {
            graph: {
              nodes: nodes.map((n) => {
                const d = n.data as {
                  nodeType?: string;
                  config?: Record<string, unknown>;
                  inputMapping?: Record<string, unknown>;
                  outputSchema?: Record<string, unknown>;
                  outputMapping?: string | null;
                };
                const base: Record<string, unknown> = {
                  nodeKey: n.id,
                  nodeType: String(d.nodeType ?? 'unknown'),
                  config: d.config ?? {},
                  inputMapping: d.inputMapping ?? {},
                  outputSchema: d.outputSchema ?? {},
                  position: { x: n.position.x, y: n.position.y },
                };
                if (typeof d.outputMapping === 'string' && d.outputMapping.length > 0) {
                  base.outputMapping = d.outputMapping;
                }
                return base;
              }),
              edges: edges.map((e) => ({
                fromNodeKey: e.source,
                toNodeKey: e.target,
                condition: typeof e.label === 'string' ? e.label : undefined,
              })),
            },
          },
        })
        .json<WorkflowVersion & { warnings?: ISaveWarning[] }>(),
    onMutate: () => {
      onValidationErrors([]);
    },
    onSuccess: async (res) => {
      onValidationErrors([]);
      onWarnings(extractSaveWarnings(res));
      try {
        await qc.invalidateQueries({ queryKey: ['workflow-versions', workflowId] });
      } catch (err) {
        console.error(err);
      }
    },
    onError: (err) => {
      readApiSaveErrors(err)
        .then((lines) => onValidationErrors(lines))
        .catch(() => onValidationErrors([]));
    },
  });
}

function useRunMutation(args: {
  client: ReturnType<typeof useClient>;
  qc: ReturnType<typeof useQueryClient>;
  workflowId: string;
  onRunStarted: (runId: string) => void;
}) {
  const { client, qc, workflowId, onRunStarted } = args;
  // DEV-16142: mutation takes an `input` object (signal payload).
  // DEV-16363: also accepts an optional `workflowVersionId` so callers can
  // trigger a specific (e.g. detached) version. Omit → API uses the
  // workflow's current version (existing behavior).
  return useMutation({
    mutationFn: (
      vars: { input?: Record<string, unknown>; workflowVersionId?: string } | undefined,
    ) =>
      client.http
        .post('v1/runs', {
          json: {
            workflowId,
            trigger: 'MANUAL',
            input: vars?.input ?? {},
            ...(vars?.workflowVersionId ? { workflowVersionId: vars.workflowVersionId } : {}),
          },
        })
        .json<{ id: string }>(),
    onSuccess: async (created) => {
      try {
        await qc.invalidateQueries({ queryKey: ['runs'] });
      } catch (err) {
        console.error(err);
      }
      onRunStarted(created.id);
    },
  });
}
